import { RefreshCw, Sparkles } from 'lucide-react';
import type { TaskAiSummary as Summary } from '@shared/types/tasks';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { formatRelative } from '@/lib/formatters';
import { cn } from '@/lib/utils';

/** The card's hover text for its AI icon: both sentences. */
export function taskSummaryText(summary: Summary): string {
  return `${summary.what} ${summary.status}`;
}

/**
 * A task's AI summary (T-318), above its activity: two sentences for someone who hasn't followed
 * it, when it was made, a spinner while a newer one is due, and Summarise again.
 */
export function TaskAiSummary({
  summary,
  dueAt,
  onSummarise,
  summarising = false,
  className,
}: {
  summary: Summary | null;
  /** When a newer summary is due (activity since this one); null when it's up to date. */
  dueAt: string | null;
  onSummarise?: () => void;
  summarising?: boolean;
  className?: string;
}) {
  const updating = Boolean(dueAt) && !summarising;
  return (
    <section aria-label="AI summary" className={cn('rounded-md border border-ai-border bg-ai-soft px-3 py-2.5 text-sm', className)}>
      <div className="flex min-h-6 items-center gap-1.5 whitespace-nowrap text-xs">
        <Sparkles className="size-3.5 shrink-0 text-ai" aria-hidden />
        <span className="font-medium text-ai-strong">AI summary</span>
        {summary ? <span className="text-2xs text-muted-foreground">· {formatRelative(summary.made_at)}</span> : null}
        {updating ? (
          // Just the spinner: the narrow activity pane has no room for the words beside Summarise again.
          <span className="inline-flex items-center text-muted-foreground" role="status" data-hint="Updating: there's been activity since this summary">
            <Spinner size="2xs" />
            <span className="sr-only">Updating…</span>
          </span>
        ) : null}
        {onSummarise ? (
          <Button
            variant="ghost"
            size="xs"
            // Tasks is a phone page: a 44px tap target there (forms-and-inputs.project.mdc, "Phone pages").
            className="ml-auto max-md:h-11"
            loading={summarising}
            loadingText="Summarising…"
            onClick={onSummarise}
            data-hint="Write a new summary now"
          >
            <RefreshCw />
            {summary ? 'Summarise again' : 'Summarise'}
          </Button>
        ) : null}
      </div>
      {summary ? (
        <p className="mt-1 leading-relaxed">{taskSummaryText(summary)}</p>
      ) : (
        <p className="mt-1 text-muted-foreground">{dueAt ? 'The first summary is on its way.' : 'No summary yet.'}</p>
      )}
    </section>
  );
}
